import { HeaderProps } from "react-table"
import { CSS, styled, StyleProps } from "../styles"
import { TableColumnSortOrder } from "./types"
import { getNewSortValue } from "./utils"
import { Tooltip } from "../Tooltip"
import { Flex } from "../Flex"

type TableHeadCellProps<T extends {}> = HeaderProps<T> & {
  header: string | number
  className?: string
  sort?: TableColumnSortOrder
  dataTestId?: string
  onSort?: (sort?: TableColumnSortOrder) => void
  tooltipContent?: string | undefined
}

const Component = <T extends {}>({
  header,
  className,
  sort,
  dataTestId,
  onSort,
  tooltipContent,
}: TableHeadCellProps<T>) => {
  let classNames = className
  if (onSort) classNames += ` rmwc-data-table__head-cell--sortable`
  if (sort) classNames += ` rmwc-data-table__head-cell--sorted`
  if (sort === `ASC`) classNames += ` rmwc-data-table__head-cell--sorted-ascending`
  if (sort === `DESC`) classNames += ` rmwc-data-table__head-cell--sorted-descending`
  const onClick = () => {
    if (!onSort) return
    onSort(getNewSortValue(sort))
  }

  const tooltip = tooltipContent ? <Flex maxWidth="200px">{tooltipContent}</Flex> : undefined

  return (
    <div
      data-testid={dataTestId}
      className={classNames}
      onClick={onClick}
      onKeyDown={onClick}
      role="columnheader"
      tabIndex={-1}
    >
      {tooltip ? (
        <Tooltip align="topLeft" content={tooltip}>
          <span style={{ cursor: `pointer` }}>{header}</span>
        </Tooltip>
      ) : (
        <span>{header}</span>
      )}
      {!!sort && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 0 24 24"
          width="24px"
          fill="currentColor"
          className="rmwc-icon rmwc-icon--component material-icons rmwc-data-table__sort-icon"
        >
          <path d="M24 24H0V0h24v24z" fill="none" opacity=".87" />
          <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z" />
        </svg>
      )}
    </div>
  )
}

Component.defaultProps = {
  sort: undefined,
  dataTestId: undefined,
  onSort: undefined,
  className: undefined,
  tooltipContent: undefined,
}

export const TableHeadCell = styled(Component)(
  (props: StyleProps): CSS => ({
    "& span": {
      display: `inline-block`,
      position: `relative`,
    },
    "&.rmwc-data-table__head-cell--sortable": {
      cursor: `pointer`,
      marginRight: props.theme.spacing(1),
    },
    "&.rmwc-data-table__head-cell--sortable:hover, &.rmwc-data-table__head-cell--sorted": {
      color: props.theme.palette.coal.dark,
    },
    "& .rmwc-data-table__sort-icon": {
      position: `absolute`,
      top: `6px`,
      marginRight: 0,
      marginLeft: `0.25em`,
    },
  })
)

export const MakeTableHeadCell = <T extends string>({
  header,
  sort,
  tooltipContent,
}: {
  header: string
  sort?: {
    key: T | undefined
    currentKey: T | undefined
    currentOrder: TableColumnSortOrder | undefined
    fn: (order: TableColumnSortOrder | undefined) => void
  }
  tooltipContent?: string
}) => (
  <TableHeadCell
    header={header}
    sort={sort ? (sort.currentKey === sort.key ? sort.currentOrder : undefined) : undefined}
    onSort={
      sort
        ? (order: TableColumnSortOrder) => {
            sort.fn(order)
          }
        : undefined
    }
    tooltipContent={tooltipContent}
  />
)

MakeTableHeadCell.defaultProps = {
  sort: undefined,
  tooltipContent: undefined,
}

export type TableHeadCellGeneratorProps<T> = {
  header: string
  property: T
  tooltipContent?: string
}

MakeTableHeadCell.defaultProps = {
  tooltipContent: undefined,
}

type MaybeSortedTableHeadCellProps<T, SK> = {
  feSortKeys: ReadonlyArray<SK>
  SortedHeaderCell: (props: TableHeadCellGeneratorProps<SK>) => JSX.Element
} & TableHeadCellGeneratorProps<T>

export const MaybeSortedTableHeadCell = <T extends string, SK extends string>({
  feSortKeys,
  SortedHeaderCell,
  ...headProps
}: MaybeSortedTableHeadCellProps<T, SK>) => {
  if (feSortKeys.includes(headProps.property as unknown as SK))
    return <SortedHeaderCell {...{ ...headProps, property: headProps.property as unknown as SK }} />
  return <TableHeadCell {...headProps} />
}

MaybeSortedTableHeadCell.defaultProps = {
  tooltipContent: undefined,
}
